import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export type MarqueeSelectionMode = 'replace' | 'add';

const DRAG_THRESHOLD_PX = 4;
const SCROLL_EDGE_PX = 48;
const SCROLL_MAX_STEP_PX = 18;

export function rectsIntersect(a: DOMRect, b: DOMRect): boolean {
  return !(a.right < b.left || a.left > b.right || a.bottom < b.top || a.top > b.bottom);
}

function isInteractiveMarqueeTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return !!target.closest(
    [
      'button',
      'input',
      'textarea',
      'select',
      'a',
      '[contenteditable="true"]',
      '.ant-checkbox',
      '.ant-checkbox-wrapper',
      '.ant-select',
      '.ant-dropdown-menu',
    ].join(', ')
  );
}

function isToolbarMarqueeTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return !!target.closest('[data-marquee-ignore="true"]');
}

function disableNativeDragAlongPath(target: EventTarget | null, scope: HTMLElement): HTMLElement[] {
  const disabled: HTMLElement[] = [];
  if (!(target instanceof Element)) return disabled;

  let node: Element | null = target;
  while (node && scope.contains(node)) {
    if (node instanceof HTMLElement && node.draggable) {
      node.draggable = false;
      disabled.push(node);
    }
    node = node.parentElement;
  }
  return disabled;
}

function restoreNativeDrag(disabled: HTMLElement[]) {
  disabled.forEach((el) => {
    el.draggable = true;
  });
}

function autoScrollRoot(scrollRoot: HTMLElement, clientX: number, clientY: number) {
  const rect = scrollRoot.getBoundingClientRect();
  if (clientY > rect.bottom - SCROLL_EDGE_PX) {
    scrollRoot.scrollTop += Math.min(SCROLL_MAX_STEP_PX, clientY - (rect.bottom - SCROLL_EDGE_PX));
  } else if (clientY < rect.top + SCROLL_EDGE_PX) {
    scrollRoot.scrollTop -= Math.min(SCROLL_MAX_STEP_PX, rect.top + SCROLL_EDGE_PX - clientY);
  }

  if (clientX > rect.right - SCROLL_EDGE_PX) {
    scrollRoot.scrollLeft += Math.min(SCROLL_MAX_STEP_PX, clientX - (rect.right - SCROLL_EDGE_PX));
  } else if (clientX < rect.left + SCROLL_EDGE_PX) {
    scrollRoot.scrollLeft -= Math.min(SCROLL_MAX_STEP_PX, rect.left + SCROLL_EDGE_PX - clientX);
  }
}

/** İmleç → scroll container içerik koordinatı (başlangıç noktası scroll ile sabit kalır) */
function clientToContent(scrollRoot: HTMLElement | null, clientX: number, clientY: number) {
  if (scrollRoot) {
    const rect = scrollRoot.getBoundingClientRect();
    return {
      x: scrollRoot.scrollLeft + (clientX - rect.left),
      y: scrollRoot.scrollTop + (clientY - rect.top),
    };
  }
  return { x: clientX + window.scrollX, y: clientY + window.scrollY };
}

/** İçerik koordinatı → viewport (fixed overlay için) */
function contentToClient(scrollRoot: HTMLElement | null, contentX: number, contentY: number) {
  if (scrollRoot) {
    const rect = scrollRoot.getBoundingClientRect();
    return {
      x: rect.left + contentX - scrollRoot.scrollLeft,
      y: rect.top + contentY - scrollRoot.scrollTop,
    };
  }
  return { x: contentX - window.scrollX, y: contentY - window.scrollY };
}

function resolveMarqueeViewportRect(
  scrollRoot: HTMLElement | null,
  startContentX: number,
  startContentY: number,
  currentClientX: number,
  currentClientY: number
) {
  const start = contentToClient(scrollRoot, startContentX, startContentY);
  const left = Math.min(start.x, currentClientX);
  const top = Math.min(start.y, currentClientY);
  const width = Math.abs(currentClientX - start.x);
  const height = Math.abs(currentClientY - start.y);
  return { left, top, width, height };
}

export type MarqueeRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

type DragState = {
  pointerId: number;
  /** Scroll container içerik koordinatı — auto-scroll sırasında sabit kalır */
  startContentX: number;
  startContentY: number;
  lastClientX: number;
  lastClientY: number;
  mode: MarqueeSelectionMode;
  active: boolean;
  startedOnCell: boolean;
  disabledDragEls: HTMLElement[];
};

type UseMarqueeSelectionOptions = {
  enabled: boolean;
  onMarqueeStart?: (mode: MarqueeSelectionMode) => void;
  onSelectionPreview: (ids: number[], mode: MarqueeSelectionMode) => void;
  onSelectionCommit: (ids: number[], mode: MarqueeSelectionMode) => void;
  onClearSelection?: () => void;
  onMarqueeGestureComplete?: () => void;
};

export function useMarqueeSelection({
  enabled,
  onMarqueeStart,
  onSelectionPreview,
  onSelectionCommit,
  onClearSelection,
  onMarqueeGestureComplete,
}: UseMarqueeSelectionOptions) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [mountNode, setMountNode] = useState<HTMLDivElement | null>(null);
  const listenRootRef = useRef<HTMLElement | null>(null);
  const scrollRootRef = useRef<HTMLElement | null>(null);
  const dragStateRef = useRef<DragState | null>(null);
  const autoScrollRafRef = useRef(0);
  const enabledRef = useRef(enabled);
  const [marqueeRect, setMarqueeRect] = useState<MarqueeRect | null>(null);
  const [isMarqueeActive, setIsMarqueeActive] = useState(false);

  const onMarqueeStartRef = useRef(onMarqueeStart);
  const onSelectionPreviewRef = useRef(onSelectionPreview);
  const onSelectionCommitRef = useRef(onSelectionCommit);
  const onClearSelectionRef = useRef(onClearSelection);
  const onMarqueeGestureCompleteRef = useRef(onMarqueeGestureComplete);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    onMarqueeStartRef.current = onMarqueeStart;
    onSelectionPreviewRef.current = onSelectionPreview;
    onSelectionCommitRef.current = onSelectionCommit;
    onClearSelectionRef.current = onClearSelection;
    onMarqueeGestureCompleteRef.current = onMarqueeGestureComplete;
  }, [onMarqueeStart, onSelectionPreview, onSelectionCommit, onClearSelection, onMarqueeGestureComplete]);

  const setContainerRef = useCallback((node: HTMLDivElement | null) => {
    containerRef.current = node;
    setMountNode(node);
  }, []);

  useLayoutEffect(() => {
    if (!mountNode) {
      scrollRootRef.current = null;
      return;
    }
    scrollRootRef.current =
      mountNode.closest<HTMLElement>('[data-app-scroll-lock]') ??
      mountNode.closest<HTMLElement>('[data-main-content-root]');
  }, [mountNode]);

  const collectIntersectingIds = useCallback((rect: DOMRect): number[] => {
    const scope = containerRef.current;
    if (!scope) return [];

    const ids: number[] = [];
    scope.querySelectorAll<HTMLElement>('[data-marquee-entry-id]').forEach((cell) => {
      const id = Number(cell.dataset.marqueeEntryId);
      if (!Number.isFinite(id)) return;
      if (rectsIntersect(rect, cell.getBoundingClientRect())) {
        ids.push(id);
      }
    });
    return ids;
  }, []);

  const buildClientRect = useCallback((left: number, top: number, width: number, height: number): DOMRect => {
    return new DOMRect(left, top, width, height);
  }, []);

  const previewForPointer = useCallback(
    (
      scrollRoot: HTMLElement | null,
      startContentX: number,
      startContentY: number,
      currentClientX: number,
      currentClientY: number,
      mode: MarqueeSelectionMode
    ) => {
      const { left, top, width, height } = resolveMarqueeViewportRect(
        scrollRoot,
        startContentX,
        startContentY,
        currentClientX,
        currentClientY
      );
      setMarqueeRect({ left, top, width, height });
      const ids = collectIntersectingIds(buildClientRect(left, top, width, height));
      onSelectionPreviewRef.current(ids, mode);
    },
    [buildClientRect, collectIntersectingIds]
  );

  const finishDrag = useCallback(
    (scrollRoot: HTMLElement | null, state: DragState) => {
      if (state.active) {
        const { left, top, width, height } = resolveMarqueeViewportRect(
          scrollRoot,
          state.startContentX,
          state.startContentY,
          state.lastClientX,
          state.lastClientY
        );
        const ids = collectIntersectingIds(buildClientRect(left, top, width, height));
        onSelectionCommitRef.current(ids, state.mode);
        onMarqueeGestureCompleteRef.current?.();
      } else if (!state.startedOnCell) {
        onClearSelectionRef.current?.();
      }
    },
    [buildClientRect, collectIntersectingIds]
  );

  const stopAutoScrollLoop = useCallback(() => {
    if (autoScrollRafRef.current) {
      cancelAnimationFrame(autoScrollRafRef.current);
      autoScrollRafRef.current = 0;
    }
  }, []);

  const beginMarqueeRef = useRef<(event: MouseEvent) => void>(() => {});

  beginMarqueeRef.current = (event: MouseEvent) => {
    const scope = containerRef.current;
    if (!enabledRef.current) return;
    if (!scope) return;
    if (dragStateRef.current) return;
    if (event.button !== 0) return;
    if (isToolbarMarqueeTarget(event.target) || isInteractiveMarqueeTarget(event.target)) return;

    const startedOnCell =
      event.target instanceof Element && !!event.target.closest('[data-marquee-entry-id]');

    const disabledDragEls = disableNativeDragAlongPath(event.target, scope);
    const scrollRoot = scrollRootRef.current;
    const startContent = clientToContent(scrollRoot, event.clientX, event.clientY);

    dragStateRef.current = {
      pointerId: 1,
      startContentX: startContent.x,
      startContentY: startContent.y,
      lastClientX: event.clientX,
      lastClientY: event.clientY,
      mode: event.metaKey || event.ctrlKey ? 'add' : 'replace',
      active: false,
      startedOnCell,
      disabledDragEls,
    };

    const runAutoScrollLoop = () => {
      const state = dragStateRef.current;
      if (!state?.active) {
        autoScrollRafRef.current = 0;
        return;
      }

      if (scrollRoot) {
        autoScrollRoot(scrollRoot, state.lastClientX, state.lastClientY);
      }

      previewForPointer(
        scrollRoot,
        state.startContentX,
        state.startContentY,
        state.lastClientX,
        state.lastClientY,
        state.mode
      );

      autoScrollRafRef.current = requestAnimationFrame(runAutoScrollLoop);
    };

    const onScrollDuringDrag = () => {
      const state = dragStateRef.current;
      if (!state?.active) return;
      previewForPointer(
        scrollRoot,
        state.startContentX,
        state.startContentY,
        state.lastClientX,
        state.lastClientY,
        state.mode
      );
    };

    const endDrag = (upEvent: MouseEvent) => {
      const state = dragStateRef.current;
      if (!state) return;
      if (upEvent.button !== 0 && upEvent.type === 'mouseup') return;

      stopAutoScrollLoop();
      scrollRoot?.removeEventListener('scroll', onScrollDuringDrag);

      finishDrag(scrollRoot, state);
      restoreNativeDrag(state.disabledDragEls);
      dragStateRef.current = null;
      setMarqueeRect(null);
      setIsMarqueeActive(false);

      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', endDrag);
    };

    const onMouseMove = (moveEvent: MouseEvent) => {
      const state = dragStateRef.current;
      if (!state) return;
      if ((moveEvent.buttons & 1) === 0) {
        endDrag(moveEvent);
        return;
      }

      state.lastClientX = moveEvent.clientX;
      state.lastClientY = moveEvent.clientY;

      const currentContent = clientToContent(scrollRoot, moveEvent.clientX, moveEvent.clientY);
      const deltaX = Math.abs(currentContent.x - state.startContentX);
      const deltaY = Math.abs(currentContent.y - state.startContentY);
      if (!state.active && deltaX < DRAG_THRESHOLD_PX && deltaY < DRAG_THRESHOLD_PX) {
        return;
      }

      if (!state.active) {
        state.active = true;
        setIsMarqueeActive(true);
        onMarqueeStartRef.current?.(state.mode);
        scrollRoot?.addEventListener('scroll', onScrollDuringDrag, { passive: true });
        if (!autoScrollRafRef.current) {
          autoScrollRafRef.current = requestAnimationFrame(runAutoScrollLoop);
        }
      }

      if (scrollRoot) {
        autoScrollRoot(scrollRoot, moveEvent.clientX, moveEvent.clientY);
      }

      moveEvent.preventDefault();
      previewForPointer(
        scrollRoot,
        state.startContentX,
        state.startContentY,
        moveEvent.clientX,
        moveEvent.clientY,
        state.mode
      );
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', endDrag);
  };

  useLayoutEffect(() => {
    if (!enabled || !mountNode) {
      listenRootRef.current = null;
      return undefined;
    }

    const listenRoot = mountNode.closest<HTMLElement>('[data-main-content-root]') ?? mountNode;
    listenRootRef.current = listenRoot;

    const onMouseDownCapture = (event: MouseEvent) => {
      if (!listenRoot.contains(event.target as Node)) return;
      beginMarqueeRef.current(event);
    };

    listenRoot.addEventListener('mousedown', onMouseDownCapture, true);
    return () => {
      listenRoot.removeEventListener('mousedown', onMouseDownCapture, true);
      listenRoot.removeAttribute('data-marquee-active');
      if (listenRootRef.current === listenRoot) {
        listenRootRef.current = null;
      }
    };
  }, [enabled, mountNode]);

  useEffect(() => {
    const listenRoot = listenRootRef.current;
    if (!listenRoot) return undefined;

    if (isMarqueeActive) {
      listenRoot.setAttribute('data-marquee-active', 'true');
    } else {
      listenRoot.removeAttribute('data-marquee-active');
    }

    return () => {
      listenRoot.removeAttribute('data-marquee-active');
    };
  }, [isMarqueeActive, mountNode]);

  useEffect(() => {
    if (!isMarqueeActive) return undefined;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    return () => {
      document.body.style.userSelect = previousUserSelect;
    };
  }, [isMarqueeActive]);

  useEffect(() => () => stopAutoScrollLoop(), [stopAutoScrollLoop]);

  return {
    containerRef,
    setContainerRef,
    marqueeRect,
    isMarqueeActive,
  };
};
